import { useState } from 'react';

import { Button, ChevronLeftIcon, ModalV2 as Modal } from '@utilities';
import type { Question, ScreenerQuestionBank } from '@api/instant_survey/types';

import { DEFAULT_TITLE, useScreenerQuestionBanks } from './utilities';
import BankView from './components/BankView';
import LibraryView from './components/LibraryView';
import PreviewView from './components/PreviewView';

import styles from './_index.module.scss';

interface ModalScreenerQuestionProps {
  onClose: () => void;
  onSelectQuestion: (question: Question) => Promise<void>;
}

const ModalScreenerQuestion = ({ onClose, onSelectQuestion }: ModalScreenerQuestionProps) => {
  const {
    data: banks,
    isError: hasBanksError,
    isLoading: isLoadingBanks,
    refetch: refetchBanks,
  } = useScreenerQuestionBanks();
  const [isAddingToSurvey, setIsAddingToSurvey] = useState(false);
  const [bank, setBank] = useState<null | ScreenerQuestionBank>(null);
  const [previewQuestion, setPreviewQuestion] = useState<null | Question>(null);

  const handleBack = () => {
    if (previewQuestion) {
      setPreviewQuestion(null);
      return;
    }

    setBank(null);
  };

  const handleAddToSurvey = async () => {
    if (!previewQuestion || isAddingToSurvey) return;

    setIsAddingToSurvey(true);
    try {
      await onSelectQuestion(previewQuestion);
    } finally {
      setIsAddingToSurvey(false);
    }
  };

  const title = previewQuestion?.screenerLabel || bank?.name || DEFAULT_TITLE;
  const isNestedView = Boolean(previewQuestion || bank);

  const actionButtons = previewQuestion
    ? [
        <Button
          isDisabled={isAddingToSurvey}
          key="modal-cancel-button"
          onClick={onClose}
          text="Cancel"
          variant="secondary"
        />,
        <Button
          isDisabled={isAddingToSurvey}
          isLoading={isAddingToSurvey}
          key="modal-confirm-button"
          onClick={handleAddToSurvey}
          text="Add to Survey"
        />,
      ]
    : [];

  return (
    <Modal
      buttons={actionButtons}
      className={styles['modal-screener-question']}
      isActive
      isScrollable
      onClose={onClose}
      size="fullscreen"
      title={title}
    >
      {isNestedView && (
        <div className={styles['modal-screener-question-view-header']}>
          <button
            aria-label="Back"
            className={styles['modal-screener-question-back']}
            onClick={handleBack}
            type="button"
          >
            <ChevronLeftIcon />
          </button>
        </div>
      )}

      {!bank && !previewQuestion && (
        <LibraryView
          banks={banks}
          isError={hasBanksError}
          isLoading={isLoadingBanks}
          onRetry={refetchBanks}
          onSelectBank={setBank}
        />
      )}
      {bank && !previewQuestion && <BankView bank={bank} onPreviewQuestion={setPreviewQuestion} />}
      {previewQuestion && <PreviewView previewQuestion={previewQuestion} />}
    </Modal>
  );
};

export default ModalScreenerQuestion;
